// AssinaturasView (manager-new) — admin global da plataforma vê TODAS as
// assinaturas recorrentes do sistema.
//
// Escopos:
//   - "platform" (offering_id NULL): assinatura pelo uso da plataforma
//     (próximo passo do projeto — hoje todos no plano free implícito)
//   - "company" (offering_id NOT NULL): compra recorrente de pacote de
//     aula (Pix mensal ou cartão), vinculada a uma organização
//
// Top: KPIs (ativas, em atraso, pausadas, total mensal recorrente, split
// Pix vs Cartão, split Plataforma vs Organização).
// Filtros: status, tipo de cobrança, escopo.
// Lista: nome do aluno, org (se houver), pacote, valor, status, próxima
// cobrança, últimas charges.

import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import {
  AlertCircle,
  Building2,
  CheckCircle2,
  CreditCard,
  Layers,
  Loader2,
  Pause,
  QrCode,
  RefreshCw,
  XCircle
} from "lucide-react";
import { Skeleton } from "@app/ui/components/skeleton";
import {
  api,
  ApiError,
  type OpenChargeRow,
  type PlatformSubscriptionRow,
  type PlatformSubscriptionSummary
} from "./api";
import { openAlert, openConfirm, openPrompt } from "./ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";

const phase = signal<"loading" | "ready" | "error">("loading");
const items = signal<PlatformSubscriptionRow[]>([]);
const summary = signal<PlatformSubscriptionSummary | null>(null);
const openCharges = signal<OpenChargeRow[]>([]);
const errMsg = signal<string | null>(null);
const statusFilter = signal<"all" | "active" | "past_due" | "paused">("all");
const billingFilter = signal<"all" | "pix_monthly" | "credit_card">("all");
const scopeFilter = signal<"all" | "platform" | "company">("all");

async function load() {
  phase.value = "loading";
  errMsg.value = null;
  try {
    const [subs, charges] = await Promise.all([
      api.listSubscriptions({
        status: statusFilter.value === "all" ? undefined : statusFilter.value,
        billingType:
          billingFilter.value === "all" ? undefined : billingFilter.value,
        scope: scopeFilter.value === "all" ? undefined : scopeFilter.value
      }),
      api.listOpenCharges()
    ]);
    items.value = subs.items;
    summary.value = subs.summary;
    openCharges.value = charges.charges;
    phase.value = "ready";
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    errMsg.value = friendlyError(e);
    phase.value = "error";
  }
}

async function markChargeAsPaid(charge: OpenChargeRow) {
  const note = await openPrompt({
    title: "Marcar como pago",
    message: `Confirmar recebimento manual de R$ ${(charge.valorCents / 100).toFixed(2).replace(".", ",")} de ${charge.userName}? (Opcional: observação de auditoria)`,
    placeholder: "Ex: Recebido via Pix em 18/05, comprovante #1234",
    confirmLabel: "Confirmar pagamento"
  });
  if (note === null) return; // cancelou
  try {
    await api.markChargeAsPaid(charge.id, note);
    await load();
  } catch (e) {
    void openAlert({
      title: "Erro ao marcar como pago",
      message: friendlyError(e)
    });
  }
}

function brl(cents: number): string {
  return (cents / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL"
  });
}

export function AssinaturasView() {
  useEffect(() => {
    void load();
  }, []);

  if (phase.value === "loading") {
    return (
      <div class="space-y-3">
        <Skeleton class="h-24 w-full" />
        <Skeleton class="h-32 w-full" />
      </div>
    );
  }
  if (phase.value === "error") {
    return (
      <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
        <AlertCircle className="mr-2 inline size-4 text-destructive" />
        {errMsg.value}
      </div>
    );
  }

  return (
    <div class="space-y-6">
      <div class="flex items-start justify-between gap-3">
        <div class="rounded-md border border-violet-500/30 bg-violet-500/5 px-4 py-3 text-sm">
          Visão global das assinaturas recorrentes. Inclui compras de pacote
          em organizações <strong>(escopo Organização)</strong> e, em breve,
          mensalidades pelo uso da plataforma{" "}
          <strong>(escopo Plataforma)</strong>.
        </div>
        <button
          type="button"
          onClick={() => void load()}
          class="inline-flex shrink-0 items-center gap-1 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent"
        >
          <RefreshCw className="size-3.5" />
          Atualizar
        </button>
      </div>

      {summary.value && <KpiStrip s={summary.value} />}

      {openCharges.value.length > 0 && (
        <OpenChargesPanel charges={openCharges.value} />
      )}

      <FiltersBar />

      {items.value.length === 0 ? (
        <div class="rounded-md border border-dashed border-border px-4 py-10 text-center text-sm text-muted-foreground">
          Nenhuma assinatura nos filtros atuais.
        </div>
      ) : (
        <ul class="space-y-3">
          {items.value.map((s) => (
            <SubscriptionRow key={s.id} sub={s} />
          ))}
        </ul>
      )}
    </div>
  );
}

function KpiStrip({ s }: { s: PlatformSubscriptionSummary }) {
  return (
    <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <Kpi
        label="Ativas"
        value={s.active}
        accent="emerald"
        icon={CheckCircle2}
      />
      <Kpi
        label="Em atraso"
        value={s.pastDue}
        accent={s.pastDue > 0 ? "destructive" : "muted"}
        icon={AlertCircle}
      />
      <Kpi
        label="Pausadas"
        value={s.paused}
        accent="amber"
        icon={Pause}
      />
      <Kpi
        label="Total mensal"
        value={brl(s.totalMonthly)}
        accent="violet"
        icon={CreditCard}
      />
      <SplitKpi
        label="Pix vs Cartão"
        a={{ label: "Pix", value: s.byType.pix_monthly, icon: QrCode }}
        b={{ label: "CC", value: s.byType.credit_card, icon: CreditCard }}
      />
      <SplitKpi
        label="Escopo"
        a={{ label: "Plataforma", value: s.byScope.platform, icon: Layers }}
        b={{ label: "Org", value: s.byScope.company, icon: Building2 }}
      />
    </div>
  );
}

function Kpi({
  label,
  value,
  accent,
  icon: Icon
}: {
  label: string;
  value: string | number;
  accent: "emerald" | "destructive" | "amber" | "violet" | "muted";
  icon: typeof CreditCard;
}) {
  const accentClass = {
    emerald: "border-emerald-500/30 bg-emerald-500/5 text-emerald-700 dark:text-emerald-300",
    destructive: "border-destructive/40 bg-destructive/5 text-destructive",
    amber: "border-amber-500/30 bg-amber-500/5 text-amber-700 dark:text-amber-300",
    violet: "border-violet-500/30 bg-violet-500/5 text-violet-700 dark:text-violet-300",
    muted: "border-border bg-card/40 text-muted-foreground"
  }[accent];
  return (
    <div class={`rounded-md border p-4 ${accentClass}`}>
      <div class="flex items-center gap-2">
        <Icon className="size-4" />
        <span class="text-[10px] font-bold uppercase tracking-wider">
          {label}
        </span>
      </div>
      <p class="mt-1 text-2xl font-bold tabular-nums">{value}</p>
    </div>
  );
}

function SplitKpi({
  label,
  a,
  b
}: {
  label: string;
  a: { label: string; value: number; icon: typeof QrCode };
  b: { label: string; value: number; icon: typeof QrCode };
}) {
  const A = a.icon;
  const B = b.icon;
  return (
    <div class="rounded-md border border-border bg-card/40 p-4">
      <span class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <div class="mt-2 flex items-center gap-3 text-sm">
        <span class="inline-flex items-center gap-1">
          <A className="size-3.5 text-muted-foreground" />
          {a.label}{" "}
          <span class="font-bold tabular-nums">{a.value}</span>
        </span>
        <span class="text-muted-foreground/50">|</span>
        <span class="inline-flex items-center gap-1">
          <B className="size-3.5 text-muted-foreground" />
          {b.label}{" "}
          <span class="font-bold tabular-nums">{b.value}</span>
        </span>
      </div>
    </div>
  );
}

function FiltersBar() {
  return (
    <div class="flex flex-wrap items-center gap-2">
      <span class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        Status
      </span>
      <Chip
        active={statusFilter.value === "all"}
        label="Todas"
        onClick={() => {
          statusFilter.value = "all";
          void load();
        }}
      />
      <Chip
        active={statusFilter.value === "active"}
        label="Ativas"
        onClick={() => {
          statusFilter.value = "active";
          void load();
        }}
      />
      <Chip
        active={statusFilter.value === "past_due"}
        label="Em atraso"
        onClick={() => {
          statusFilter.value = "past_due";
          void load();
        }}
      />
      <Chip
        active={statusFilter.value === "paused"}
        label="Pausadas"
        onClick={() => {
          statusFilter.value = "paused";
          void load();
        }}
      />
      <span class="mx-2 h-4 w-px bg-border" />
      <span class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        Cobrança
      </span>
      <Chip
        active={billingFilter.value === "all"}
        label="Tudo"
        onClick={() => {
          billingFilter.value = "all";
          void load();
        }}
      />
      <Chip
        active={billingFilter.value === "pix_monthly"}
        label="Pix mensal"
        onClick={() => {
          billingFilter.value = "pix_monthly";
          void load();
        }}
      />
      <Chip
        active={billingFilter.value === "credit_card"}
        label="Cartão"
        onClick={() => {
          billingFilter.value = "credit_card";
          void load();
        }}
      />
      <span class="mx-2 h-4 w-px bg-border" />
      <span class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        Escopo
      </span>
      <Chip
        active={scopeFilter.value === "all"}
        label="Tudo"
        onClick={() => {
          scopeFilter.value = "all";
          void load();
        }}
      />
      <Chip
        active={scopeFilter.value === "platform"}
        label="Plataforma"
        onClick={() => {
          scopeFilter.value = "platform";
          void load();
        }}
      />
      <Chip
        active={scopeFilter.value === "company"}
        label="Organização"
        onClick={() => {
          scopeFilter.value = "company";
          void load();
        }}
      />
    </div>
  );
}

function Chip({
  active,
  label,
  onClick
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      class={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${
        active
          ? "border-primary bg-primary text-primary-foreground"
          : "border-border bg-background hover:border-primary/40 hover:text-foreground"
      }`}
    >
      {label}
    </button>
  );
}

function SubscriptionRow({ sub }: { sub: PlatformSubscriptionRow }) {
  const [expanded, setExpanded] = useState(false);
  const isPix = sub.billingType === "pix_monthly";
  return (
    <li class="rounded-md border border-border bg-card">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        class="flex w-full items-start gap-3 p-4 text-left hover:bg-accent/30"
      >
        <div
          class={`grid size-10 shrink-0 place-items-center rounded-md ${
            isPix
              ? "bg-emerald-500/15 text-emerald-600"
              : "bg-violet-500/15 text-violet-600"
          }`}
        >
          {isPix ? <QrCode className="size-5" /> : <CreditCard className="size-5" />}
        </div>
        <div class="min-w-0 flex-1">
          <div class="flex items-baseline gap-2">
            <span class="truncate font-medium">{sub.userName}</span>
            <span class="truncate text-xs text-muted-foreground">
              {sub.userEmail}
            </span>
          </div>
          <div class="mt-0.5 flex flex-wrap items-baseline gap-2 text-xs">
            <span class="font-medium">{sub.pacoteNomeSnap}</span>
            {sub.scope === "platform" ? (
              <span class="inline-flex items-center gap-1 rounded-full bg-violet-500/15 px-2 py-0.5 text-[10px] font-semibold text-violet-700 dark:text-violet-300">
                <Layers className="size-3" />
                Plataforma
              </span>
            ) : (
              sub.companyName && (
                <span class="inline-flex items-center gap-1 rounded-full bg-blue-500/15 px-2 py-0.5 text-[10px] font-semibold text-blue-700 dark:text-blue-300">
                  <Building2 className="size-3" />
                  {sub.companyName}
                </span>
              )
            )}
          </div>
          <div class="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
            <span class="font-semibold tabular-nums">
              {brl(sub.valorCents)} /mês
            </span>
            <span class="text-muted-foreground">·</span>
            <span class="text-muted-foreground">
              {isPix ? "Pix mensal" : "Cartão"}
            </span>
            <span class="text-muted-foreground">·</span>
            <StatusBadge status={sub.status} />
            <span class="text-muted-foreground">·</span>
            <span class="text-muted-foreground">
              Próxima:{" "}
              <span class="font-medium text-foreground tabular-nums">
                {new Date(sub.nextChargeAt).toLocaleDateString("pt-BR")}
              </span>
            </span>
            {sub.failedAttempts > 0 && (
              <>
                <span class="text-muted-foreground">·</span>
                <span class="font-medium text-destructive">
                  {sub.failedAttempts} falha{sub.failedAttempts !== 1 ? "s" : ""}
                </span>
              </>
            )}
          </div>
        </div>
      </button>

      {expanded && sub.recentCharges.length > 0 && (
        <div class="border-t border-border bg-background/40 px-4 py-3">
          <div class="mb-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Últimas cobranças
          </div>
          <ul class="space-y-1.5 text-xs">
            {sub.recentCharges.map((c) => (
              <li key={c.id} class="flex items-center gap-2">
                <ChargeIcon status={c.status} />
                <span class="text-muted-foreground tabular-nums">
                  {new Date(c.chargedAt).toLocaleString("pt-BR")}
                </span>
                <span class="font-medium tabular-nums">{brl(c.valorCents)}</span>
                {c.methodPriority !== null && (
                  <span class="text-muted-foreground">
                    ({c.methodPriority === 0 ? "principal" : "reserva"})
                  </span>
                )}
                {c.failureReason && (
                  <span class="ml-1 truncate text-amber-600">
                    {c.failureReason}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </li>
  );
}

function StatusBadge({ status }: { status: PlatformSubscriptionRow["status"] }) {
  const map = {
    active: { label: "ativa", class: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" },
    paused: { label: "pausada", class: "bg-amber-500/15 text-amber-700 dark:text-amber-300" },
    past_due: { label: "em atraso", class: "bg-destructive/15 text-destructive" },
    canceled: { label: "cancelada", class: "bg-muted text-muted-foreground" }
  }[status];
  return (
    <span
      class={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${map.class}`}
    >
      {map.label}
    </span>
  );
}

function ChargeIcon({
  status
}: {
  status: PlatformSubscriptionRow["recentCharges"][number]["status"];
}) {
  if (status === "succeeded")
    return <CheckCircle2 className="size-3.5 text-emerald-600" />;
  if (status === "failed") return <XCircle className="size-3.5 text-destructive" />;
  return <Loader2 className="size-3.5 text-muted-foreground" />;
}

// Painel "Cobranças abertas" — lista charges pending/failed pra reconciliação.
// Cada linha tem botão "Marcar como pago" que abre prompt de observação e
// chama o endpoint manager. Útil pra quando o aluno paga por fora (Pix
// estático, depósito, etc) e o admin precisa confirmar manualmente.
function OpenChargesPanel({ charges }: { charges: OpenChargeRow[] }) {
  return (
    <section class="rounded-md border border-amber-500/30 bg-amber-500/5 p-4">
      <header class="mb-3 flex items-center justify-between gap-3">
        <div class="flex items-center gap-2">
          <AlertCircle className="size-4 text-amber-600" />
          <h2 class="text-sm font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300">
            Cobranças abertas ({charges.length})
          </h2>
        </div>
        <span class="text-[10px] text-muted-foreground">
          Use "Marcar como pago" quando receber por fora da plataforma.
        </span>
      </header>
      <ul class="divide-y divide-border overflow-hidden rounded-md border border-border bg-card/40">
        {charges.map((c) => (
          <OpenChargeRowItem key={c.id} charge={c} />
        ))}
      </ul>
    </section>
  );
}

function OpenChargeRowItem({ charge }: { charge: OpenChargeRow }) {
  const isPix = charge.provider === "pix_manual";
  return (
    <li class="flex items-center gap-3 px-3 py-2 text-xs">
      {charge.status === "failed" ? (
        <XCircle className="size-4 shrink-0 text-destructive" />
      ) : (
        <Loader2 className="size-4 shrink-0 text-amber-500" />
      )}
      <div class="min-w-0 flex-1">
        <div class="flex items-baseline gap-2">
          <span class="truncate font-medium">{charge.userName}</span>
          <span class="truncate text-muted-foreground">{charge.userEmail}</span>
        </div>
        <div class="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-muted-foreground">
          <span>{charge.pacoteNomeSnap}</span>
          <span>·</span>
          <span class="font-semibold text-foreground tabular-nums">
            {(charge.valorCents / 100).toLocaleString("pt-BR", {
              style: "currency",
              currency: "BRL"
            })}
          </span>
          <span>·</span>
          <span>{isPix ? "Pix" : "Cartão"}</span>
          <span>·</span>
          <span class="tabular-nums">
            {new Date(charge.chargedAt).toLocaleDateString("pt-BR")}
          </span>
          {charge.failureReason && (
            <>
              <span>·</span>
              <span class="text-destructive">{charge.failureReason}</span>
            </>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={() => markChargeAsPaid(charge)}
        title="Marcar como pago"
        class="inline-flex shrink-0 items-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/5 px-2 py-1 text-[11px] font-medium text-emerald-700 transition-colors hover:bg-emerald-500/15 dark:text-emerald-300"
      >
        <CheckCircle2 className="size-3" />
        Marcar pago
      </button>
    </li>
  );
}
